import { FC, useEffect, useState } from 'react';
import { useRoutes } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { CssBaseline, ThemeProvider, Snackbar, Alert } from '@mui/material';
import SplashScreen from './components/SplashScreen';
import useAuth from './hooks/useAuth';
import useScrollReset from './hooks/useScrollReset';
import useSettings from './hooks/useSettings';
import routes from './routes';
import { createCustomTheme } from './theme';
import './css.css';
import { LogLevel, Logger } from './utils/logger';
import useSnackbar from './hooks/useSnackbar';

const App: FC<React.PropsWithChildren<unknown>> = () => {
  const content = useRoutes(routes);
  const { settings } = useSettings();
  const [connectionStatus, setConnectionStatus] = useState<string>('');
  const auth = useAuth();
  const { isSnackActive, snackMessage, snackSeverity, snackTimeout, closeSnackBar } = useSnackbar();

  window.addEventListener('offline', () => {
    setConnectionStatus('offline');
  });

  window.addEventListener('online', () => {
    setConnectionStatus('online');
  });

  const handleClose = (reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setConnectionStatus('');
  };

  useEffect(() => {
    Logger.init(LogLevel.INFO);
  }, []);

  const handleSnackClose = (reason) => {
    if (reason === 'timeout' || reason === 'clickaway' || reason === 'escapeKeyDown') {
      return;
    }
    closeSnackBar();
  };

  useScrollReset();

  const theme = createCustomTheme({
    direction: settings.direction,
    responsiveFontSizes: settings.responsiveFontSizes,
    roundedCorners: settings.roundedCorners,
    theme: settings.theme,
  });

  return (
    <ThemeProvider theme={theme}>
      <Snackbar open={connectionStatus == 'offline'} onClose={handleClose}>
        <Alert severity="error" elevation={6} variant="filled">
          No internet connection.
        </Alert>
      </Snackbar>
      <Snackbar open={connectionStatus == 'online'} autoHideDuration={3000} onClose={handleClose}>
        <Alert severity="success" elevation={6} variant="filled">
          Connected
        </Alert>
      </Snackbar>

      <Snackbar
        open={isSnackActive}
        sx={{ top: '100px !important' }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={snackTimeout}
        onClose={handleSnackClose}
      >
        <Alert onClose={handleSnackClose} severity={snackSeverity} variant="filled" sx={{ width: '100%' }}>
          {snackMessage}
        </Alert>
      </Snackbar>

      <CssBaseline />
      <Toaster position="top-center" />
      {auth.isInitialized ? content : <SplashScreen />}
    </ThemeProvider>
  );
};

export default App;
